import { useState, type FormEvent } from 'react';
import { api, type NewAttachment, type TicketMessage, type TicketStatus } from '../api.ts';
import { message } from '../format.ts';
import { kilobytes } from './image.ts';
import { ImagePicker } from './ImagePicker.tsx';
import type { PreparedImage } from './prepareImage.ts';
import {
  BODY_MAX,
  consoleStatusLabel,
  formatWhen,
  hasErrors,
  oldestFirst,
  platformStatusLabel,
  validateReply,
  type TicketErrors,
} from './tickets.ts';
import './support.css';

/**
 * The pieces the console and the platform area share: a ticket's status, its
 * thread, and the box to answer it. The two sides differ in what they call a
 * status and in who may close a ticket, not in how a conversation looks.
 */

export type Audience = 'console' | 'platform';

export const StatusChip = ({ status, audience }: { status: TicketStatus; audience: Audience }) => (
  <span className={`sp-status sp-status-${status}`}>
    {audience === 'console' ? consoleStatusLabel(status) : platformStatusLabel(status)}
  </span>
);

export const Thread = ({ messages }: { messages: readonly TicketMessage[] }) => (
  <ol className="sp-thread" aria-label="Messages, oldest first">
    {oldestFirst(messages).map((entry) => (
      <li key={entry.id} className={entry.fromStaff ? 'sp-message sp-message-staff' : 'sp-message'}>
        <p className="sp-message-head">
          <span className="sp-author">{entry.authorName}</span>
          {/* Words, not only the tinted card: which side said something is the
              one thing a reader of a thread must never have to infer. */}
          {entry.fromStaff && <span className="sp-staff-mark">DriftWatch support</span>}
          <time className="note" dateTime={entry.createdAt}>
            {formatWhen(entry.createdAt)}
          </time>
        </p>
        <p className="sp-body">{entry.body}</p>
        {entry.attachments.length > 0 && (
          <ul className="sp-attachments">
            {entry.attachments.map((attachment, index) => (
              <li key={attachment.id}>
                <a
                  href={api.supportAttachmentUrl(attachment.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={api.supportAttachmentUrl(attachment.id)}
                    alt={`Screenshot ${index + 1} from ${entry.authorName}, opens full size`}
                    loading="lazy"
                  />
                </a>
                <span className="note num">{kilobytes(attachment.size)}</span>
              </li>
            ))}
          </ul>
        )}
      </li>
    ))}
  </ol>
);

interface ReplyFormProps {
  readonly idPrefix: string;
  readonly label: string;
  readonly onSend: (reply: { body: string; attachment?: NewAttachment }) => Promise<void>;
}

export const ReplyForm = ({ idPrefix, label, onSend }: ReplyFormProps) => {
  const [body, setBody] = useState('');
  const [image, setImage] = useState<PreparedImage | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [sending, setSending] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // Shown only after a first attempt: an empty box is not a mistake until
  // somebody tries to send it.
  const errors: TicketErrors = attempted ? validateReply(body) : {};
  const bodyId = `${idPrefix}-body`;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    setFailure(null);
    if (hasErrors(validateReply(body))) return;
    setSending(true);
    try {
      await onSend(image === null ? { body } : { body, attachment: image.attachment });
      setBody('');
      setImage(null);
      setAttempted(false);
    } catch (cause) {
      setFailure(message(cause));
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="sp-reply" onSubmit={(event) => void submit(event)} noValidate>
      <div className="field">
        <label htmlFor={bodyId}>{label}</label>
        <textarea
          id={bodyId}
          value={body}
          maxLength={BODY_MAX}
          disabled={sending}
          aria-invalid={errors.body === undefined ? undefined : true}
          aria-describedby={errors.body === undefined ? undefined : `${bodyId}-error`}
          onChange={(event) => setBody(event.target.value)}
        />
        {errors.body !== undefined && (
          <p className="sp-field-error" id={`${bodyId}-error`}>
            {errors.body}
          </p>
        )}
      </div>

      <ImagePicker
        id={`${idPrefix}-image`}
        value={image}
        onChange={setImage}
        disabled={sending}
        onBusy={setPreparing}
      />

      <div className="toolbar">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={sending || preparing}
          aria-busy={sending || undefined}
        >
          {sending ? 'Sending…' : 'Send reply'}
        </button>
        {failure !== null && (
          <p className="sp-field-error" role="alert">
            {failure}
          </p>
        )}
      </div>
    </form>
  );
};
